import { ColDef, ColGroupDef, ColumnState } from "ag-grid-community";
import { aggridHeaderClass } from "src/components/common/common";

export interface GridCustomColumnDefsParams<TData> {
	baseColumnDefs: (ColDef<TData> | ColGroupDef<TData>)[];
	columnState: ColumnState[] | null;
	headerNames: Record<string, string>;
	fallbackHeaderClass?: string;
	/** false면 pinned는 applyColumnState(API)만 사용 — colDef merge 제외 */
	mergePinnedFromState?: boolean;
}

function hasGroupHeaders<TData>(
	defs: (ColDef<TData> | ColGroupDef<TData>)[]
): boolean {
	return defs.some(def => 'children' in def && Array.isArray(def.children));
}

function isColGroupDef<TData>(
	def: ColDef<TData> | ColGroupDef<TData>
): def is ColGroupDef<TData> {
	return 'children' in def && Array.isArray(def.children);
}

function mergeFlatColDefFromState<TData>(
	baseColumn: ColDef<TData>,
	state: ColumnState,
	mergePinnedFromState: boolean,
): ColDef<TData> {
	const merged: ColDef<TData> = {
		...baseColumn,
		width: state.width || baseColumn.width,
		hide: state.hide || false,
	};
	if (mergePinnedFromState) {
		merged.pinned = state.pinned === null ? null : (state.pinned ?? baseColumn.pinned ?? null);
	}
	return merged;
}

function createFlatFallbackColDef<TData>(
	state: ColumnState,
	headerNames: Record<string, string>,
	fallbackHeaderClass: string,
): ColDef<TData> {
	return {
		headerName: headerNames[state.colId] || state.colId,
		colId: state.colId,
		field: (state.colId !== 'rowIndex' ? state.colId : undefined) as any,
		width: state.width || 100,
		headerClass: fallbackHeaderClass,
	} as ColDef<TData>;
}

function buildFlatColumnDefs<TData>({
	baseColumnDefs,
	columnState,
	headerNames,
	fallbackHeaderClass,
	mergePinnedFromState,
}: {
	baseColumnDefs: (ColDef<TData> | ColGroupDef<TData>)[];
	columnState: ColumnState[];
	headerNames: Record<string, string>;
	fallbackHeaderClass: string;
	mergePinnedFromState: boolean;
}): ColDef<TData>[] {
	const flatBaseColumnDefs = baseColumnDefs as ColDef<TData>[];

	return columnState.map((state): ColDef<TData> => {
		const baseColumn = flatBaseColumnDefs.find(col => col.colId === state.colId);

		if (baseColumn) {
			return mergeFlatColDefFromState(baseColumn, state, mergePinnedFromState);
		}

		return createFlatFallbackColDef(state, headerNames, fallbackHeaderClass);
	}).filter(Boolean);
}

function buildGroupColumnDefs<TData>({
	baseColumnDefs,
	columnState,
	mergePinnedFromState,
}: {
	baseColumnDefs: (ColDef<TData> | ColGroupDef<TData>)[];
	columnState: ColumnState[];
	mergePinnedFromState: boolean;
}): (ColDef<TData> | ColGroupDef<TData>)[] {
	const stateMap = new Map(columnState.map(state => [state.colId, state]));

	/**
	 * 그룹 헤더 구조는 그대로 유지하고 실제 자식 컬럼에만 저장 상태를 적용한다.
	 * 너비 맞추기 후 Store가 갱신되어도 선언된 기본 width로 되돌아가지 않도록
	 * 각 자식 컬럼의 colId를 기준으로 너비·표시·고정·정렬 상태를 복원한다.
	 */
	const applyState = (
		defs: (ColDef<TData> | ColGroupDef<TData>)[]
	): (ColDef<TData> | ColGroupDef<TData>)[] =>
		defs.map(def => {
			if (isColGroupDef(def)) {
				return {
					...def,
					children: applyState(def.children),
				} as ColGroupDef<TData>;
			}

			const column = def;
			const colId = column.colId;
			const state = colId ? stateMap.get(colId) : undefined;
			if (!state) return column;

			const merged: ColDef<TData> = {
				...column,
				width: state.width ?? column.width,
				hide: state.hide ?? column.hide,
				sort: state.sort ?? column.sort,
				sortIndex: state.sortIndex ?? column.sortIndex,
			};
			if (mergePinnedFromState) {
				merged.pinned = state.pinned === null ? null : (state.pinned ?? column.pinned);
			}
			return merged;
		});

	return applyState(baseColumnDefs);
}

/**
 * AG Grid의 columnState를 기반으로 컬럼 정의를 동적으로 생성하는 함수.
 * - 평면 컬럼: columnState 순서로 재배열하고 width/hide/pinned 병합
 * - 그룹 헤더(ColGroupDef): 구조 유지, 리프 컬럼에만 상태 병합
 */
export function GridCustomColumnDefs<TData>({
	baseColumnDefs,
	columnState,
	headerNames,
	fallbackHeaderClass = aggridHeaderClass,
	mergePinnedFromState = true,
}: GridCustomColumnDefsParams<TData>): (ColDef<TData> | ColGroupDef<TData>)[] {
	if (columnState === null) {
		return baseColumnDefs;
	}

	if (hasGroupHeaders(baseColumnDefs)) {
		// 그룹 그리드는 현재 정의된 자식 컬럼만 사용한다.
		// 로컬스토리지에 남아 있는 삭제된 과거 colId를 빈 컬럼으로 복원하지 않는다.
		return buildGroupColumnDefs({
			baseColumnDefs,
			columnState,
			mergePinnedFromState,
		});
	}

	return buildFlatColumnDefs({
		baseColumnDefs,
		columnState,
		headerNames,
		fallbackHeaderClass,
		mergePinnedFromState,
	});
}
